import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import LexicalRichEditor from './ui/LexicalRichEditorLazy';
import OsScroll from './ui/OsScroll';
import { recordsContext } from '../extensions/bundled/documents/documentsStore';
import { useTabsStore, openDocumentTab } from '../store/tabsStore';
import { useAuthStore } from '../store/authStore';
import { useDocumentPath } from '../extensions/bundled/documents/useDocumentPath';
import useTabWorkspace from './document-editor/useTabWorkspace';
import useFullWidth from './document-editor/useFullWidth';
import { buildSubpageSlashExtra } from '../extensions/bundled/documents/insertSubpage';
import { PageLinkContext } from './ui/lexicalNodes/PageLinkContext';
import SyncTooLargeBanner from './SyncTooLargeBanner';
import { useCloudReadOnly } from '../plans/useCloudReadOnly';
import { useDocumentBody } from './document-editor/useDocumentBody';
import { useDocumentCollab } from './document-editor/useDocumentCollab';
import { useDocumentMeta } from './document-editor/useDocumentMeta';
import { useDocumentActions } from './document-editor/useDocumentActions';
import { useDocsLayoutStore, DOCS_BREAKPOINTS } from './document-editor/docsLayoutStore';
import { useMediaQuery } from './document-editor/useMediaQuery';
import { DOC_THEME } from './document-editor/docTypography';
import { buildCalloutSlashExtras } from './document-editor/calloutSlash';
import HeadingIdsPlugin from './document-editor/HeadingIdsPlugin';
import DocumentToolbar from './document-editor/DocumentToolbar';
import TitleBlock from './document-editor/TitleBlock';
import OutlineRail from './document-editor/OutlineRail';
import CollabConflictBanner from './document-editor/CollabConflictBanner';
import DocumentActionModals from './document-editor/DocumentActionModals';
import './DocumentEditorTab.css';
import './document-editor/docTypography.css';
import './document-editor/DocumentToolbar.css';
import './document-editor/OutlineRail.css';

const PERSONAL_SCOPE = { type: 'personal' };
const DOC_PLUGINS = <HeadingIdsPlugin />;

/**
 * One open document (documents redesign, 13.10.2026): the 48 px toolbar
 * (breadcrumb, "Edited … · N words", history, Share, outline, More), the
 * reading column — title block (chips, title, author line) over the Lexical
 * body at the 760 px measure — and the "On this page" rail.
 *
 * The parts live in ./document-editor/: load + autosave (useDocumentBody),
 * team collab + reconcile (useDocumentCollab), the facts the toolbar and
 * title block show (useDocumentMeta), the menu actions (useDocumentActions).
 * This component wires them together.
 *
 * Team documents (tab.scope.type === 'team') additionally get live block-level
 * collab; personal documents never touch it — `collab` stays null and no
 * presence/ops traffic is sent.
 */
export default function DocumentEditorTab({ documentId }) {
  const { t } = useTranslation();
  // Two narrow selectors (not the whole tab object) so toggling this tab's own
  // `hasUnsavedChanges` flag — which happens on every settled edit — doesn't
  // itself force a re-render here: `.scope` and `.label` keep their identity
  // across that spread, so zustand's reference-equality bails out.
  const scope = useTabsStore((s) => s.documentTabs.find((t2) => t2.documentId === documentId)?.scope) || PERSONAL_SCOPE;
  const tabLabel = useTabsStore((s) => s.documentTabs.find((t2) => t2.documentId === documentId)?.label);
  const userName = useAuthStore((s) => s.user?.name || '');
  const bodyRef = useRef(null);
  // Plans v2 card 3.4: a CLOUD document of a plan without cloud sync is
  // read-only here; "Download to this device" in its menu makes it editable.
  const cloudReadOnly = useCloudReadOnly();
  const readOnly = cloudReadOnly && (!scope.type || scope.type === 'personal');

  // One scope-tree read serving the breadcrumb, the folder chip AND the
  // PageLink chips (see useDocumentPath).
  const { ancestors, node: treeNode, resolve: resolveTreeDoc } = useDocumentPath(documentId, scope);
  useTabWorkspace(documentId, scope, treeNode);
  const pageLinkCtx = useMemo(
    () => ({
      resolve: resolveTreeDoc,
      onOpen: ({ docId, scope: linkScope }) => {
        const info = resolveTreeDoc(docId);
        openDocumentTab({ documentId: docId, label: (info && info.title) || t('docSidebar.untitled'), scope: linkScope || scope });
      },
    }),
    [resolveTreeDoc, scope, t]
  );
  const slashExtras = useMemo(
    () => [buildSubpageSlashExtra({ documentId, scope }), ...buildCalloutSlashExtras()],
    [documentId, scope]
  );
  // Enables media insertion (DocMediaPlugin + slash Media group) for this
  // document. Memoized on the scope's SCALARS so the plugin never re-registers
  // its commands/rewriter over an identity-only change.
  const media = useMemo(
    () => ({ docId: documentId, scope }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentId, scope.type, scope.teamId]
  );

  const session = useDocumentBody({ documentId, scope, tabLabel });
  const { collab, docViewers, collabConflict, dismissCollabConflict } = useDocumentCollab(session, { documentId, scope });
  const meta = useDocumentMeta({ documentId, scope, subscribeBody: session.subscribeBody, ready: session.status === 'ready' });
  const flush = useCallback(async () => {
    await session.flushTitle();
    await session.flushBody();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.flushTitle, session.flushBody]);
  const actions = useDocumentActions({ documentId, scope, title: session.title, flush, snapshot: session.snapshot });

  // Layout (cards 2.4 + 2.6): the rail sits inline from 1100 px; narrower, the
  // toolbar toggle floats it over the page. Below 900 px the sidebar is an
  // overlay opened from the toolbar.
  const wide = useMediaQuery(`(min-width: ${DOCS_BREAKPOINTS.outline}px)`);
  const narrowSidebar = !useMediaQuery(`(min-width: ${DOCS_BREAKPOINTS.sidebar}px)`);
  const outlineOpen = useDocsLayoutStore((s) => s.outlineOpen);
  const setOutlineOpen = useDocsLayoutStore((s) => s.setOutlineOpen);
  const overlayOpen = useDocsLayoutStore((s) => s.overlayOpen);
  const setOverlayOpen = useDocsLayoutStore((s) => s.setOverlayOpen);
  const [floatingOutline, setFloatingOutline] = useState(false);
  const [fullWidth, toggleFullWidth] = useFullWidth(documentId);
  const outlineShown = wide ? outlineOpen : floatingOutline;

  // Ctrl/Cmd D duplicates, Ctrl/Cmd P prints — the more-menu's kbd chips.
  const onShortcut = (e) => {
    if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;
    const key = e.key.toLowerCase();
    if (key !== 'd' && key !== 'p') return;
    e.preventDefault();
    actions.onAction(key === 'd' ? 'duplicate' : 'print');
  };

  if (session.status === 'loading') {
    // Blank editor-colored shell (no centered "Loading…" text) so opening a
    // document reads as an instant transition.
    return <div className="doc-editor" aria-busy="true" />;
  }
  if (session.status === 'notfound') {
    return (
      <div className="doc-editor-empty">
        <FileText size={28} className="doc-editor-empty-icon" aria-hidden />
        <span>{t('docEditor.notFound')}</span>
      </div>
    );
  }

  return (
    <div className="doc-editor doc-reading" onKeyDownCapture={onShortcut}>
      <DocumentToolbar
        documentId={documentId}
        scope={scope}
        scopeKey={recordsContext(scope)?.workspaceId}
        title={session.title}
        ancestors={ancestors}
        meta={meta}
        actions={actions}
        viewers={docViewers}
        narrowSidebar={narrowSidebar}
        onToggleSidebar={() => setOverlayOpen(!overlayOpen)}
        outlineOpen={outlineShown}
        onToggleOutline={() => (wide ? setOutlineOpen(!outlineOpen) : setFloatingOutline((v) => !v))}
        fullWidth={fullWidth}
        onToggleFullWidth={toggleFullWidth}
      />

      <SyncTooLargeBanner scopeId={documentId} kind="document" />
      {readOnly && (
        <div className="sync-too-large-banner" role="status" data-testid="cloud-read-only-banner">
          <span className="sync-too-large-text">{t('cloudLocal.readOnly')}</span>
        </div>
      )}
      {collabConflict && <CollabConflictBanner onDismiss={dismissCollabConflict} />}

      <div className="doc-editor-main">
        <OsScroll className="doc-editor-body-scroll">
          <div className={`doc-editor-body doc-reading-column${fullWidth ? ' doc-reading-column--full' : ''}`} ref={bodyRef}>
            <TitleBlock
              title={session.title}
              onTitleChange={session.onTitleChange}
              ancestors={ancestors}
              scope={scope}
              meta={meta}
              userName={userName}
              readOnly={readOnly}
            />
            <div
              onFocus={() => {
                session.editorFocusedRef.current = true;
              }}
              onBlur={() => {
                session.editorFocusedRef.current = false;
              }}
            >
              <PageLinkContext.Provider value={pageLinkCtx}>
                <LexicalRichEditor
                  key={`${documentId}:${session.editorNonce}`}
                  ref={session.editorRef}
                  fallback={<div className="doc-editor-loading" />}
                  value={session.initialBody}
                  valueBlocks={session.initialBlocks}
                  blockIdSeed={documentId}
                  onChange={session.onBodyChange}
                  onBlocksChange={session.onBodyBlocksChange}
                  collab={collab}
                  disabled={readOnly}
                  placeholder={t('docEditor.placeholder')}
                  blockHandles
                  slashExtras={slashExtras}
                  media={media}
                  theme={DOC_THEME}
                  plugins={DOC_PLUGINS}
                />
              </PageLinkContext.Provider>
            </div>
          </div>
        </OsScroll>
        {outlineShown && (
          <OutlineRail
            documentId={documentId}
            bodyRef={bodyRef}
            subscribeBody={session.subscribeBody}
            variant={wide ? 'inline' : 'overlay'}
          />
        )}
      </div>

      <DocumentActionModals actions={actions} documentId={documentId} scope={scope} title={session.title} />
    </div>
  );
}
